import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import React, { type FunctionComponent, useCallback } from 'react';
import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { messages } from '../messages';

const toggleWrapperStyles = xcss({
  marginBlock: 'space.negative.025',
  marginInline: 'space.negative.075'
});

const tooltipTriggerStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'color.background.neutral.subtle',
  borderRadius: 'radius.small',
  display: 'inline-flex',
  padding: 'space.050',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  }
});

export interface AllowMultiToggleProps {
  labelMessage: MessageDescriptor;
  onChange: (value: boolean) => void;
  tooltipMessage?: MessageDescriptor;
  value: boolean;
}

const AllowMultiToggle: FunctionComponent<AllowMultiToggleProps> = ({
  labelMessage,
  onChange,
  tooltipMessage,
  value
}) => {
  const { formatMessage } = useIntl();
  const label = formatMessage(labelMessage);
  const tooltip = tooltipMessage ? formatMessage(tooltipMessage) : null;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked);
    },
    [onChange]
  );

  return (
    <Stack space="space.025">
      <Text size="small" weight="bold" color="color.text.subtle">
        {formatMessage(messages.allowMultiToggleSectionLabel)}
      </Text>
      <Inline alignBlock="center" space="space.075" spread="space-between">
        <Inline alignBlock="center" space="space.050">
          <Text color="color.text.subtle">{label}</Text>
          {tooltip && (
            <Tooltip content={tooltip} position="top">
              {(tooltipProps) => (
                <Pressable
                  {...tooltipProps}
                  aria-label={tooltip}
                  xcss={tooltipTriggerStyles}
                >
                  <InformationCircleIcon
                    label=""
                    size="small"
                    color={token('color.icon.subtle')}
                  />
                </Pressable>
              )}
            </Tooltip>
          )}
        </Inline>
        <Box xcss={toggleWrapperStyles}>
          <Toggle
            id="allow-multiple-values-toggle"
            isChecked={value}
            label={label}
            onChange={handleChange}
          />
        </Box>
      </Inline>
    </Stack>
  );
};

export default AllowMultiToggle;
